import breakpoints, { Breakpoint } from '@/breakpoints';
import { isMobile, isServer, setMobile, unsetMobile } from '@/helpers';
import { useCallback, useEffect, useState } from 'react';

type ScreenDeviceSize = 'mobile' | 'tablet' | 'desktop';

const breakpointMapping: { [key in Breakpoint]: ScreenDeviceSize } = {
  sm: 'mobile',
  md: 'tablet',
  lg: 'desktop',
  xl: 'desktop',
  '2xl': 'desktop',
} as const;

function getScreenSize() {
  if (isServer) return { width: isMobile() ? breakpoints.sm : breakpoints.md };
  const { innerWidth: width } = window;
  return { width };
}

function setDeviceBreakpointClass(breakpoint: Breakpoint) {
  const existingBreakpoint = document.documentElement.classList.value.match(/screen-(mobile|tablet|desktop)/)?.[0];

  if (existingBreakpoint) {
    const shouldUpdateBreakpointClass = breakpointMapping[breakpoint] === existingBreakpoint.split('-')[1];

    if (!shouldUpdateBreakpointClass) {
      return;
    }
    document.documentElement.classList.remove(existingBreakpoint);
  }

  let className: `screen-${ScreenDeviceSize}` = 'screen-mobile';

  switch (breakpoint) {
    case 'md':
      className = 'screen-tablet';
      break;
    case 'lg':
    case 'xl':
    case '2xl':
      className = 'screen-desktop';
      break;
    default:
      break;
  }

  document.documentElement.classList.add(className);
}

const useMobileView = () => {
  const [windowSize, setWindowSize] = useState<{ width: number }>(getScreenSize);
  const [isMobileView, setIsMobileView] = useState<boolean>(isMobile);

  // Debounced resize handler
  const handleResize = useCallback(() => {
    const newSize = getScreenSize();
    setWindowSize((prev) => (prev.width !== newSize.width ? newSize : prev));
  }, []);

  useEffect(() => {
    const deviceBreakpoint = (() => {
      if (windowSize.width < breakpoints.sm) {
        return 'sm';
      } else if (windowSize.width < breakpoints.md) {
        return 'md';
      } else if (windowSize.width < breakpoints.lg) {
        return 'lg';
      } else if (windowSize.width < breakpoints.xl) {
        return 'xl';
      } else {
        return '2xl';
      }
    })();

    setDeviceBreakpointClass(deviceBreakpoint);

    if (windowSize.width < breakpoints.md) {
      setMobile();
      setIsMobileView(true);
      return;
    }
    unsetMobile();
    setIsMobileView(false);
  }, [windowSize]);

  useEffect(() => {
    const debounce = <T extends (...args: any[]) => void>(
      func: T,
      delay: number,
    ): ((...args: Parameters<T>) => void) => {
      let timeout: NodeJS.Timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), delay);
      };
    };

    const debouncedResize = debounce(handleResize, 50);

    window.addEventListener('resize', debouncedResize);
    return () => window.removeEventListener('resize', debouncedResize);
  }, [handleResize]);

  return { isMobileView, windowSize };
};

export default useMobileView;
